import React, { useMemo, useState } from 'react';
import { createAttempt, submitReading } from '../../api/exams';
import { setPartProgress, computeMistakes, scoreReadingPart } from './_progress';
import { T } from './_theme';
import {
  ReadingFrame, PartTopBar, FeedbackCard, PrimaryBtn, GhostBtn, NudgeBanner,
  readArabicPref, readingHomeHref,
} from './_primitives';

// FET Reading — Part 2 · Gap Fill
//
// Content shape:
//   { topic: 'Dolphins',
//     passage: 'People love dolphins... Dolphins are also [GAP1] of the cleverest...',
//     gaps: [{ n, options: [str, str, str], correct: 'one',
//              why_en, why_ar, mistake_en, mistake_ar, evidence,
//              examples: [{good|bad, text}] }, ...]
//   }
// Submission key per gap: gap_{i} → option string

const GAP_RE = /\[GAP(\d+)\]/g;

// Split the passage on [GAPn] tokens. Returns an alternating list of text
// fragments and gap markers, e.g. [{kind:'text', value:'...'}, {kind:'gap', n:1}, ...]
function splitPassage(passage) {
  if (!passage) return [];
  const out = [];
  let last = 0;
  let m;
  GAP_RE.lastIndex = 0;
  while ((m = GAP_RE.exec(passage)) !== null) {
    if (m.index > last) out.push({ kind: 'text', value: passage.slice(last, m.index) });
    out.push({ kind: 'gap', n: Number(m[1]) });
    last = m.index + m[0].length;
  }
  if (last < passage.length) out.push({ kind: 'text', value: passage.slice(last) });
  return out;
}

export default function Part2Screen({
  isMobile, navigate, exam, loading,
  onContinueToNextPart, nextPartLabel,
}) {
  const part = useMemo(
    () => (exam?.reading_parts || []).find((p) => p.part_number === 2),
    [exam],
  );
  const gaps = useMemo(() => (part?.content?.gaps || []).filter(Boolean), [part]);
  const total = gaps.length;
  const segments = useMemo(() => splitPassage(part?.content?.passage || ''), [part]);

  const [arabicOn, setArabicOn] = useState(readArabicPref);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [finalResult, setFinalResult] = useState(null);
  const [error, setError] = useState('');

  const persistArabic = (next) => {
    setArabicOn(next);
    try { window.localStorage.setItem('fet_reading_arabic', next ? '1' : '0'); } catch {}
  };

  if (loading || !exam) {
    return <ReadingFrame isMobile={isMobile}><div style={{ padding: 32, color: T.ink2, fontFamily: T.sans }}>Loading reading exam…</div></ReadingFrame>;
  }
  if (!part || !total) {
    return (
      <ReadingFrame isMobile={isMobile}>
        <PartTopBar partNumber={2} partTitle="Gap Fill" currentIdx={0} total={1} onBack={() => navigate(readingHomeHref())} />
        <div style={{ padding: 24, color: T.ink2, fontFamily: T.sans, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 700, marginBottom: 6, color: T.ink }}>No gaps configured for Part 2</div>
          <div>An admin needs to add a passage with at least one gap from the admin panel.</div>
        </div>
      </ReadingFrame>
    );
  }

  if (finalResult) {
    const pct = finalResult.max ? Math.round((finalResult.score / finalResult.max) * 100) : 0;
    return (
      <ReadingFrame isMobile={isMobile}>
        <PartTopBar partNumber={2} partTitle="Gap Fill" currentIdx={total - 1} total={total} onBack={() => navigate(readingHomeHref())} />
        <div style={{ padding: 24 }}>
          <div style={{ background: T.surface, border: `1px solid ${T.hairline}`, borderRadius: 18, padding: 22, textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: T.ink3, marginBottom: 12 }}>Part 2 result</div>
            <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 48, fontWeight: 700, color: T.blue, fontVariantNumeric: 'tabular-nums' }}>{finalResult.score}</span>
              <span style={{ fontSize: 20, color: T.ink3, fontWeight: 600 }}>/ {finalResult.max}</span>
            </div>
            <div style={{ fontSize: 14, color: T.ink2, marginTop: 8 }}>You scored <strong style={{ color: T.ink }}>{pct}%</strong>.</div>
            <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <GhostBtn onClick={() => { setAnswers({}); setCurrentIdx(0); setFinalResult(null); setError(''); }}>Try again</GhostBtn>
              {onContinueToNextPart ? (
                <PrimaryBtn full={false} onClick={onContinueToNextPart}>{nextPartLabel || 'Next part →'}</PrimaryBtn>
              ) : (
                <PrimaryBtn full={false} onClick={() => navigate('/dashboard')}>Back to dashboard</PrimaryBtn>
              )}
            </div>
          </div>
        </div>
      </ReadingFrame>
    );
  }

  const gap = gaps[currentIdx] || {};
  const opts = Array.isArray(gap.options) ? gap.options : [];
  const correctVal = String(gap.correct || '').toLowerCase();
  const picked = answers[`gap_${currentIdx}`] || null;
  const answered = picked !== null;
  const isCorrect = answered && picked.toLowerCase() === correctVal;
  const isLast = currentIdx === total - 1;

  const pickAnswer = (val) => {
    if (answered) return;
    setAnswers((prev) => ({ ...prev, [`gap_${currentIdx}`]: val }));
  };

  const goNext = async () => {
    if (!isLast) {
      setCurrentIdx((i) => Math.min(total - 1, i + 1));
      return;
    }
    setSubmitting(true);
    setError('');
    const local = scoreReadingPart(2, part?.content, answers);
    const result = {
      score: local.score,
      max: local.max || total,
      mistakes: computeMistakes(2, part?.content, answers),
    };
    setFinalResult(result);
    setPartProgress(2, result);
    try {
      const attempt = await createAttempt(exam.id, 'practice', 'reading');
      await submitReading(attempt.id, { '2': answers });
    } catch (err) {
      const apiErr = err?.response?.data;
      setError(apiErr?.error || 'Could not save your attempt to the server. Your local score is shown.');
    } finally {
      setSubmitting(false);
    }
  };

  // Render passage with inline gap chips. Each chip styled by its state:
  //   filled (already answered) → green
  //   current → blue dashed
  //   pending → muted
  const renderPassage = () => (
    <div style={{ fontSize: 14, lineHeight: 1.95, fontFamily: T.serif, color: T.ink }}>
      {segments.map((seg, i) => {
        if (seg.kind === 'text') return <span key={i}>{seg.value}</span>;
        const idx = seg.n - 1;
        if (idx === currentIdx) {
          return (
            <span key={i} style={{
              display: 'inline-block', margin: '0 2px', padding: '1px 14px',
              background: T.blueBg, border: `2px dashed ${T.blue}`,
              color: T.blueInk, borderRadius: 6,
              fontWeight: 700, fontFamily: T.sans,
            }}>({seg.n}) ?</span>
          );
        }
        const filledVal = answers[`gap_${idx}`];
        if (filledVal) {
          return (
            <span key={i} style={{
              display: 'inline-block', margin: '0 2px', padding: '1px 8px',
              background: T.okBg, border: `1px solid ${T.ok}`,
              color: T.ok, borderRadius: 6,
              fontWeight: 600, fontFamily: T.sans,
            }}>{filledVal}</span>
          );
        }
        return (
          <span key={i} style={{
            display: 'inline-block', margin: '0 2px', padding: '1px 8px',
            background: T.bgSunk, border: `1px solid ${T.hairlineInk}`,
            color: T.ink3, borderRadius: 6,
            fontWeight: 600, fontFamily: T.sans,
          }}>({seg.n})</span>
        );
      })}
    </div>
  );

  return (
    <ReadingFrame isMobile={isMobile}>
      <PartTopBar partNumber={2} partTitle="Gap Fill" currentIdx={currentIdx} total={total} onBack={() => navigate(readingHomeHref())} />
      <div style={{ flex: 1, padding: 16 }}>
        {part?.content?.topic && (
          <div style={{ fontSize: 11, color: T.ink3, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 6 }}>
            Passage · {part.content.topic}
          </div>
        )}
        <div style={{ background: T.surface, borderRadius: 14, border: `1px solid ${T.hairline}`, padding: 14 }}>
          {renderPassage()}
        </div>

        <div style={{ marginTop: 14, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, color: T.ink3, fontWeight: 700 }}>
          Gap {gap.n || currentIdx + 1}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
          {opts.map((o) => {
            let state = 'idle';
            if (answered) {
              if (o.toLowerCase() === correctVal) state = 'correct';
              else if (o === picked) state = 'incorrect';
              else state = 'dim';
            }
            const c = {
              idle:      { bg: T.surface, bd: T.hairlineInk, fg: T.ink },
              correct:   { bg: T.okBg, bd: T.ok, fg: T.ok },
              incorrect: { bg: T.missBg, bd: T.miss, fg: T.miss },
              dim:       { bg: T.surface, bd: T.hairline, fg: T.ink3 },
            }[state];
            return (
              <button
                key={o}
                type="button"
                onClick={() => pickAnswer(o)}
                disabled={answered}
                style={{
                  padding: '16px 10px',
                  background: c.bg,
                  border: `1.5px solid ${c.bd}`,
                  borderRadius: 14,
                  color: c.fg,
                  fontSize: 16, fontWeight: 700,
                  fontFamily: T.serif, textAlign: 'center',
                  cursor: answered ? 'default' : 'pointer',
                }}
              >{o}</button>
            );
          })}
        </div>

        {answered && (
          <FeedbackCard
            correct={isCorrect}
            header={isCorrect ? `Nice — '${correctVal}' is correct` : `Almost — the answer is '${correctVal}'`}
            picked={picked}
            lang={arabicOn ? 'ar' : 'en'}
            onLangChange={(v) => persistArabic(v === 'ar')}
            simple={arabicOn ? gap.why_ar : gap.why_en}
            mistake={!isCorrect ? (arabicOn ? gap.mistake_ar : gap.mistake_en) : null}
            examples={Array.isArray(gap.examples) ? gap.examples : null}
            evidence={gap.evidence}
          />
        )}

        {error && <div style={{ marginTop: 12, fontSize: 13, color: T.miss }}>{error}</div>}
        {!answered && <div style={{ marginTop: 14 }}><NudgeBanner>Pick a word for gap {gap.n || currentIdx + 1} to continue.</NudgeBanner></div>}
      </div>

      <div style={{ padding: '12px 16px', borderTop: `1px solid ${T.hairline}`, background: T.surface, display: 'flex', gap: 10 }}>
        <GhostBtn disabled={currentIdx === 0} onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}>← Previous</GhostBtn>
        <div style={{ flex: 1 }} />
        <PrimaryBtn full={false} disabled={!answered || submitting} onClick={goNext}>
          {submitting ? 'Saving…' : isLast ? 'Finish part →' : 'Next gap →'}
        </PrimaryBtn>
      </div>
    </ReadingFrame>
  );
}
